import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import Styles from './ChatComparison.module.scss'
import { useChatComparison } from '../ChatComparisonContext'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { PostPreview } from '../PostPreview'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ModifiedActionBar } from './ModifiedActionBar'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'

import { ChatComparisonPayload } from '../ChatComparisonContext'

interface ChatComparisonProps {
  displayedFile: AlmanacFileShowItem
}

const POST_PREVIEW_VERTICAL_SPACE_OFFSET = 320

export const ChatComparison = ({ displayedFile }: ChatComparisonProps) => {
  const { comparisons, isApplying, isDismissed, applyOriginal, applyModified } = useChatComparison()
  const [viewedMessageId, setViewedMessageId] = useState<number | null>(null)
  const prevNewestIdRef = useRef<number | undefined>(comparisons[0]?.messageId)

  // Auto-advance to newest when a new comparison appears at the front
  useEffect(() => {
    const newestId = comparisons[0]?.messageId
    if (newestId !== prevNewestIdRef.current) {
      prevNewestIdRef.current = newestId
      setViewedMessageId(null)
    }
  }, [comparisons])

  const currentIndex = viewedMessageId
    ? Math.max(
        0,
        comparisons.findIndex((c) => c.messageId === viewedMessageId),
      )
    : 0
  const comparison = comparisons[currentIndex] ?? null

  const handlePrevious = useCallback(() => {
    const prevIndex = currentIndex - 1
    if (prevIndex >= 0) {
      setViewedMessageId(comparisons[prevIndex].messageId)
    }
  }, [currentIndex, comparisons])

  const handleNext = useCallback(() => {
    const nextIndex = currentIndex + 1
    if (nextIndex < comparisons.length) {
      setViewedMessageId(comparisons[nextIndex].messageId)
    }
  }, [currentIndex, comparisons])

  const modifiedFile = useMemo(
    () => buildModifiedFile(comparison, comparisons, displayedFile),
    [comparison, comparisons, displayedFile],
  )

  const handleUseModified = useCallback(() => {
    if (!comparison) return

    // Deduplicate by slideIndex: one replacement per slide.
    // The currently viewed comparison wins for its slide; for other slides,
    // the newest comparison (first in array) with that slideIndex is used.
    const bySlide = new Map<number | undefined, ChatComparisonPayload>()
    for (const c of comparisons) {
      if (!bySlide.has(c.slideIndex)) {
        bySlide.set(c.slideIndex, c)
      }
    }
    bySlide.set(comparison.slideIndex, comparison)
    applyModified([...bySlide.values()])
  }, [comparisons, comparison, applyModified])

  if (isDismissed) return null

  return (
    <div className={Styles.root}>
      <div className={Styles.content}>
        <div className={Styles.header}>
          <Heading level={5} color="var(--dark-90)">
            Choose a variation
          </Heading>
          <Text variant="metadata" color="var(--dark-60)">
            Click on your preferred version or request further changes.
          </Text>
        </div>

        <div className={Styles.columns}>
          <div className={Styles.column}>
            <div className={Styles.originalLabel}>
              <Text variant="secondary" color="var(--dark-90)">
                Original
              </Text>
            </div>
            <div className={Styles.previewContainer}>
              <PostPreview
                almanacFile={displayedFile}
                isLoading={false}
                isActive
                showVideoControls
                videoFit="cover"
                windowHeightOffset={POST_PREVIEW_VERTICAL_SPACE_OFFSET}
                previewPlatform={ExternalIntegrationEnum.Instagram}
              />
            </div>
            <Button variant="secondary" size="md" fullWidth className={Styles.columnButton} onPress={applyOriginal}>
              Use Original
            </Button>
          </div>

          <div className={Styles.column}>
            {comparison && (
              <ModifiedActionBar
                comparison={comparison}
                currentIndex={currentIndex}
                totalCount={comparisons.length}
                onPrevious={handlePrevious}
                onNext={handleNext}
              />
            )}
            <div className={Styles.previewContainer}>
              {modifiedFile ? (
                <PostPreview
                  almanacFile={modifiedFile}
                  isLoading={false}
                  isActive
                  showVideoControls
                  videoFit="cover"
                  windowHeightOffset={POST_PREVIEW_VERTICAL_SPACE_OFFSET}
                  previewPlatform={ExternalIntegrationEnum.Instagram}
                />
              ) : (
                <div className={Styles.emptyState}>No comparison available</div>
              )}
            </div>
            <Button
              variant="primary"
              size="md"
              fullWidth
              className={Styles.columnButton}
              isDisabled={isApplying}
              onPress={handleUseModified}
            >
              {'Use Modified'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

type MediaObjectArray = NonNullable<AlmanacFileShowItem['scheduled_post']>['media_objects']

const rebuildFileWithSlideUrls = (
  displayedFile: AlmanacFileShowItem,
  baseMediaObjects: MediaObjectArray,
  slideUrls: Array<string | undefined>,
  topLevelImageUrl: string,
): AlmanacFileShowItem => {
  const rebuiltMediaObjects = slideUrls.map((url, index) => {
    const base = baseMediaObjects[index] ?? baseMediaObjects[0]
    return {
      ...base,
      source_url: url ?? base?.source_url,
      thumbnail_url: url ?? base?.thumbnail_url,
    }
  })
  return {
    ...displayedFile,
    image_url: topLevelImageUrl,
    scheduled_post: {
      ...displayedFile.scheduled_post,
      media_objects: rebuiltMediaObjects,
    },
  } as AlmanacFileShowItem
}

export const buildModifiedFile = (
  comparison: ChatComparisonPayload | null,
  comparisons: ChatComparisonPayload[],
  displayedFile: AlmanacFileShowItem,
): AlmanacFileShowItem | null => {
  if (!comparison) return null
  if (comparison.mediaType === 'video' && !comparison.videoUrl) return null

  const mediaObjects = displayedFile.scheduled_post?.media_objects ?? []

  const slidePreviewUrls = comparison.slidePreviewUrls
  if (slidePreviewUrls?.length) {
    return rebuildFileWithSlideUrls(displayedFile, mediaObjects, slidePreviewUrls, slidePreviewUrls[0])
  }

  const generationSlides = comparison.generationData?.slides as Array<{ image_url?: string }> | undefined
  if (generationSlides?.length) {
    const urls = generationSlides.map((slide) => slide.image_url)
    return rebuildFileWithSlideUrls(displayedFile, mediaObjects, urls, comparison.previewUrl)
  }

  const modifiedMediaObjects = mediaObjects.map((mediaObject, index) => {
    if (comparison.slideIndex === undefined) {
      return {
        ...mediaObject,
        source_url: comparison.videoUrl ?? comparison.previewUrl,
        thumbnail_url: comparison.previewUrl,
      }
    }

    const slideComparison =
      comparison.slideIndex === index ? comparison : comparisons.find((c) => c.slideIndex === index)

    if (!slideComparison) return mediaObject

    return {
      ...mediaObject,
      source_url: slideComparison.videoUrl ?? slideComparison.previewUrl,
      thumbnail_url: slideComparison.previewUrl,
    }
  })

  return {
    ...displayedFile,
    image_url: comparison.previewUrl,
    scheduled_post: {
      ...displayedFile.scheduled_post,
      media_objects: modifiedMediaObjects,
    },
  } as AlmanacFileShowItem
}
